/*
  CSO Extended delivery tick box: behaviour (Oct 2026)

  Loaded by snippets/cso-extended-delivery.liquid, which holds the markup, the
  settings and the full explanation. In short: the tick box above Add to cart
  is shown only when the option the shopper chose states a wait in its name,
  or, when none of the product's options does, when the description states
  one. While it is shown, Add to cart is blocked until it is ticked.

  Fail-safe: the box starts hidden and switched off, so if this file fails to
  load or run, nothing is shown and nothing is blocked.
*/
(function () {
  'use strict';
  var KEY = '__csoExtendedDelivery';
  var api = window[KEY];

  if (!api) {
    api = window[KEY] = { boxes: [] };

    var LOCK_CLASS = 'cso-ed-locked';
    var APP_WAIT_MS = 12000; /* if the options app never renders, stop waiting for it */
    var TOAST_TEXT = 'Please tick the delivery box first';
    var NOT_OPTIONS = '.cso-ed, .cso-renewal-reminder';

    /* ----------------------------------------------------------------
       Reading a wait out of wording.
       A wait is a number of weeks ("3-4 weeks", "4 -6 weeks", "6-8 week",
       "3 weeks", "three weeks", "4-week") or the words "special order",
       "made to order" or "back order".
       ---------------------------------------------------------------- */
    /* En dash, em dash and non-breaking space, as character codes so that
       this file stays plain ASCII. */
    var EN_DASH = String.fromCharCode(8211);
    var EM_DASH = String.fromCharCode(8212);
    var ANY_DASH = new RegExp('[' + EN_DASH + EM_DASH + ']', 'g');
    var NBSP = new RegExp(String.fromCharCode(160), 'g');
    var WORD_NUMBERS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12 };
    var NUMBER = '(\\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)';
    var WEEKS = new RegExp('(^|[^a-z0-9.,])' + NUMBER + '(?:\\s*(?:[-' + EN_DASH + EM_DASH + ']|to)\\s*' + NUMBER + ')?[\\s-]*w(?:ee)?ks?(?![a-z])', 'gi');
    var SPECIAL_ORDER = /special[\s-]*order|made[\s-]*to[\s-]*order|back[\s-]*order/i;
    /* "Class 3 - 4 weeks": the 3 belongs to "Class", it is not "3 to 4 weeks". */
    var LABEL_NUMBER = /(?:class|ccl|size|grade|level|type|style|model|no\.?|#)\s*$/i;
    var UP_TO = /up\s+to\s*$/i;
    /* Description sentences only count when they read like a delivery
       statement, not like wearing advice ("wear for 2 weeks after ..."). */
    var DELIVERY_WORDS = /\bdeliver(?:y|ies|ed)\b|dispatch|\bship|arriv|\border|\bwait|\b(?:in|of)\s+stock\b|\ballow|\beta\b|lead[\s-]*time|supplier|\bimport(?:ed|s)?\b|\bcomes?\s+from|\bcoming\s+from|\btakes?\b|\brequir/i;
    var USAGE_WORDS = /\bwear|\bworn\b|\bwash|\breplace|\badjust|\bused\s+to\b|surgery|pregnan/i;

    var toNumber = function (value) {
      var key = String(value).toLowerCase();
      return WORD_NUMBERS[key] || parseInt(key, 10) || 0;
    };

    var isLonger = function (a, b) {
      if (!b) return true;
      return a.max !== b.max ? a.max > b.max : a.min > b.min;
    };

    /* null when the text states no wait, otherwise { min, max, upTo } in
       weeks: "3-4 weeks" is 3 to 4, "4 weeks" is 4 to 4, and "special
       order" without a number is 0 to 0. The longest one in the text wins. */
    var readWait = function (text) {
      text = String(text || '');
      var best = null;
      var match;
      WEEKS.lastIndex = 0;
      while ((match = WEEKS.exec(text))) {
        var before = text.slice(0, match.index + match[1].length);
        var first = toNumber(match[2]);
        var second = match[3] ? toNumber(match[3]) : 0;
        var wait;
        if (second && first < second && !LABEL_NUMBER.test(before)) {
          wait = { min: first, max: second, upTo: false };
        } else {
          wait = { min: second || first, max: second || first, upTo: !second && UP_TO.test(before) };
        }
        if (wait.max && isLonger(wait, best)) best = wait;
      }
      if (!best && SPECIAL_ORDER.test(text)) best = { min: 0, max: 0, upTo: false };
      return best;
    };

    /* "3-4 weeks" (with the dash it is given), "about 3 weeks", "up to 3 weeks"; '' when there is no number. */
    var formatWait = function (wait, dash) {
      if (!wait || !wait.max) return '';
      var unit = wait.max === 1 ? ' week' : ' weeks';
      if (wait.min !== wait.max) return wait.min + dash + wait.max + unit;
      return (wait.upTo ? 'up to ' : 'about ') + wait.max + unit;
    };

    /* The description as separate sentences. It is parsed as inert text:
       nothing in it is loaded or run. */
    var sentencesOf = function (html) {
      var text = '';
      try {
        var doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
        [].forEach.call(doc.querySelectorAll('script, style'), function (el) { el.parentNode.removeChild(el); });
        [].forEach.call(doc.querySelectorAll('br, p, li, div, h1, h2, h3, h4, h5, h6, tr, td, th'), function (el) {
          el.parentNode.insertBefore(doc.createTextNode('\n'), el);
          if (el.tagName !== 'BR') el.appendChild(doc.createTextNode('\n'));
        });
        text = (doc.body && doc.body.textContent) || '';
      } catch (e) {
        text = String(html || '').replace(/<[^>]*>/g, '\n');
      }
      var sentences = [];
      text.replace(NBSP, ' ').split(/\n+/).forEach(function (line) {
        line.split(/[.!?]\s+(?=[A-Z])/).forEach(function (sentence) {
          if (sentence.trim()) sentences.push(sentence);
        });
      });
      return sentences;
    };

    /* The wait the description states for the product as a whole (null when it states none). */
    var describedWait = function (html) {
      var best = null;
      var stated = false;
      sentencesOf(html).forEach(function (sentence) {
        var wait = readWait(sentence);
        if (!wait || USAGE_WORDS.test(sentence)) return;
        if (!SPECIAL_ORDER.test(sentence) && !DELIVERY_WORDS.test(sentence)) return;
        stated = true;
        if (wait.max && isLonger(wait, best)) best = wait;
      });
      return stated ? best || { min: 0, max: 0, upTo: false } : null;
    };

    /* ----------------------------------------------------------------
       The option fields on the page
       ---------------------------------------------------------------- */
    var checkboxOf = function (box) { return box.querySelector('.cso-ed__checkbox'); };
    var rootOf = function (box) { return box.closest('product-info') || document; };

    var optionFields = function (box) {
      return [].filter.call(rootOf(box).querySelectorAll('[name^="properties["]'), function (field) {
        return !field.closest(NOT_OPTIONS);
      });
    };

    var labelOf = function (field) {
      return field.labels && field.labels[0] ? field.labels[0].textContent : '';
    };

    /* Every choice a field offers, as text. Typed-in fields offer none. */
    var offeredTexts = function (field) {
      if (field.tagName === 'SELECT') {
        return [].map.call(field.options, function (option) { return option.text + ' ' + option.value; });
      }
      if (field.type === 'radio' || field.type === 'checkbox') return [labelOf(field) + ' ' + field.value];
      return [];
    };

    /* A field the options app is currently hiding (it depends on another choice). */
    var hiddenByLogic = function (field) {
      var card = field.closest('.product-customizer-option');
      return !!card && (card.classList.contains('conditional-logic-hidden') || card.style.display === 'none');
    };

    /* The choice made in a field, as text ('' when nothing is chosen). */
    var chosenText = function (field) {
      if (hiddenByLogic(field)) return '';
      if (field.tagName === 'SELECT') {
        if (!field.value) return '';
        var option = field.options[field.selectedIndex];
        return (option ? option.text : '') + ' ' + field.value;
      }
      if (field.type === 'radio' || field.type === 'checkbox') {
        return field.checked ? labelOf(field) + ' ' + field.value : '';
      }
      return '';
    };

    /* Does any option of this product state a wait in its name? */
    var anyOptionStatesWait = function (box) {
      if (box.csoEdOptionWaits) return true;
      var found = optionFields(box).some(function (field) {
        return offeredTexts(field).some(function (text) { return !!readWait(text); });
      });
      if (found) box.csoEdOptionWaits = true;
      return found;
    };

    /* The longest wait stated by the options the shopper has chosen (null when none of them states one). */
    var chosenWait = function (box) {
      var form = box.closest('form');
      var groups = {};
      optionFields(box).forEach(function (field) {
        (groups[field.name] = groups[field.name] || []).push(field);
      });
      var best = null;
      Object.keys(groups).forEach(function (name) {
        /* The options app shows its fields outside the product form and
           keeps hidden copies inside it that are only filled in when the
           item is added. Read the ones the shopper actually uses. */
        var live = groups[name].filter(function (field) { return !form || !form.contains(field); });
        (live.length ? live : groups[name]).forEach(function (field) {
          var wait = readWait(chosenText(field));
          if (wait && isLonger(wait, best)) best = wait;
        });
      });
      return best;
    };

    /* The timeframe to print for what is chosen right now, or null when
       the tick box does not apply to it. */
    var resolve = function (box) {
      var brand = (box.getAttribute('data-brand-time') || '').trim();
      var other = (box.getAttribute('data-other-time') || '').trim() || 'longer than usual';
      if (anyOptionStatesWait(box)) {
        var chosen = chosenWait(box);
        if (!chosen) return null;
        var stated = formatWait(chosen, EN_DASH);
        var brandFirst = box.getAttribute('data-brand-first') === 'true';
        return (brandFirst ? brand || stated : stated || brand) || other;
      }
      if (box.csoEdDescriptionWait) {
        return brand || formatWait(box.csoEdDescriptionWait, EN_DASH) || other;
      }
      return null;
    };

    var isFilled = function (field) {
      if (field.type === 'checkbox' || field.type === 'radio') return field.checked;
      var value = field.value;
      return !!value && String(value).trim() !== '' && value !== 'Please choose one';
    };

    /* Required option fields that still have no value. Uses the rule the
       Add to cart flow already applies; the copy below only runs if that
       shared function is missing. */
    var missingOptions = function (box) {
      var own = checkboxOf(box);
      var list = null;
      if (typeof window.csoMissingRequiredFields === 'function') {
        try { list = window.csoMissingRequiredFields(); } catch (e) { list = null; }
      }
      if (!list) {
        list = [];
        var groups = {};
        [].forEach.call(rootOf(box).querySelectorAll('select, input, textarea'), function (field) {
          var key = field.name || field.id;
          if (!key) return;
          (groups[key] = groups[key] || []).push(field);
        });
        Object.keys(groups).forEach(function (key) {
          var copies = groups[key];
          if (!copies.some(function (f) { return f.hasAttribute('required'); })) return;
          if (copies.some(isFilled)) return;
          list.push(copies[0]);
        });
      }
      return [].filter.call(list, function (field) { return field !== own; });
    };

    /* Products with option fields: wait until the options app has put its
       fields on the page before deciding anything. */
    var optionsReady = function (box) {
      if (box.getAttribute('data-expects-options') !== 'true') return true;
      if (optionFields(box).length) return true;
      return Date.now() - box.csoEdStart > APP_WAIT_MS;
    };

    var setClass = function (el, name, on) {
      if (el && el.classList.contains(name) !== on) el.classList.toggle(name, on);
    };

    var clearError = function (box) {
      setClass(box, 'cso-ed--error', false);
      var error = box.querySelector('.cso-ed__error');
      if (error && !error.hidden) error.hidden = true;
    };

    /* Show or hide the box, print the timeframe and grey out Add to cart,
       all to match what is chosen right now. */
    var update = function (box) {
      var checkbox = checkboxOf(box);
      if (!checkbox || !document.documentElement.contains(box)) return;
      var time = optionsReady(box) ? resolve(box) : null;
      var show = time !== null && missingOptions(box).length === 0;

      if (show) {
        var value = 'Accepted - ' + time.replace(ANY_DASH, '-');
        if (checkbox.value !== value) {
          checkbox.value = value;
          /* a different timeframe needs a fresh tick */
          if (checkbox.checked) checkbox.checked = false;
        }
        var slot = box.querySelector('[data-cso-ed-time]');
        if (slot && slot.textContent !== time) slot.textContent = time;
      } else if (checkbox.checked) {
        checkbox.checked = false;
      }
      /* Switched off while it does not apply, so nothing is saved on the order. */
      if (checkbox.disabled === show) checkbox.disabled = !show;

      if (box.hidden === show) {
        box.hidden = !show;
        if (show) {
          box.classList.remove('cso-ed--reveal');
          void box.offsetWidth;
          box.classList.add('cso-ed--reveal');
        }
      }
      var locked = show && !checkbox.checked;
      setClass(box.closest('form'), LOCK_CLASS, locked);
      var sectionId = box.getAttribute('data-section-id');
      setClass(sectionId ? document.getElementById('CsoStickyBtn-' + sectionId) : null, LOCK_CLASS, locked);
      if (checkbox.checked || !show) clearError(box);
    };

    var schedule = function (box) {
      clearTimeout(box.csoEdTimer);
      box.csoEdTimer = setTimeout(function () { update(box); }, 150);
    };

    var toast;
    var toastTimer;
    var showToast = function (text) {
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'cso-ed-toast';
        toast.setAttribute('aria-hidden', 'true');
        document.body.appendChild(toast);
      }
      toast.textContent = text;
      toast.classList.add('is-shown');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { toast.classList.remove('is-shown'); }, 3500);
    };

    /* Add to cart was pressed without the tick: point the shopper at the box. */
    var nudge = function (box) {
      var checkbox = checkboxOf(box);
      setClass(box, 'cso-ed--error', true);
      var error = box.querySelector('.cso-ed__error');
      if (error && error.hidden) error.hidden = false;
      var viewport = function () { return window.innerHeight || document.documentElement.clientHeight; };
      var rect = box.getBoundingClientRect();
      var offScreen = rect.top < 90 || rect.bottom > viewport() - 150;
      if (offScreen) {
        try { box.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { box.scrollIntoView(); }
        /* If the smooth scroll was interrupted, jump there instead. */
        setTimeout(function () {
          var now = box.getBoundingClientRect();
          if (now.top < 0 || now.bottom > viewport()) {
            try { box.scrollIntoView({ block: 'center' }); } catch (e) { box.scrollIntoView(); }
          }
        }, 900);
      }
      setTimeout(function () {
        try { checkbox.focus({ preventScroll: true }); } catch (e) {}
      }, offScreen ? 450 : 0);
      showToast(TOAST_TEXT);
    };

    /* Stops an add-to-cart attempt when the box applies, the options are
       all chosen and the box is not ticked. In every other case this stays
       out of the way, so the existing "choose your options" guidance runs. */
    var gate = function (event, form) {
      if (!form || typeof form.querySelector !== 'function') return;
      var box = form.querySelector('[data-cso-ed]');
      if (!box) return;
      var checkbox = checkboxOf(box);
      if (!checkbox) return;
      update(box);
      if (box.hidden || checkbox.checked) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      nudge(box);
    };

    /* Capture phase on window: runs before the theme's and the options
       app's own click / submit handlers, including the sticky bar, which
       forwards its click to the real Add to cart button. */
    window.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var button = target.closest('button, input');
      if (!button || !button.form) return;
      var type = (button.getAttribute('type') || (button.tagName === 'BUTTON' ? 'submit' : '')).toLowerCase();
      if (type !== 'submit' && type !== 'image') return;
      try { gate(event, button.form); } catch (e) {}
    }, true);

    window.addEventListener('submit', function (event) {
      try { gate(event, event.target); } catch (e) {}
    }, true);

    var scheduleAll = function () {
      [].forEach.call(document.querySelectorAll('[data-cso-ed]'), function (box) {
        if (box.csoEdReady) schedule(box);
        else api.setup(box);
      });
    };
    document.addEventListener('change', scheduleAll, true);
    document.addEventListener('input', scheduleAll, true);
    window.addEventListener('pageshow', scheduleAll);

    api.setup = function (box) {
      if (!box || box.csoEdReady) return;
      box.csoEdReady = true;
      box.csoEdStart = Date.now();
      api.boxes.push(box);

      /* What the product states, from the copy of its option names and
         description that the page was rendered with. */
      var data = {};
      try {
        var holder = box.querySelector('script[data-cso-ed-data]');
        data = (holder && JSON.parse(holder.textContent)) || {};
      } catch (e) { data = {}; }
      box.csoEdOptionWaits = [].concat(data.fields || []).some(function (item) {
        try {
          var field = typeof item === 'string' ? JSON.parse(item) : item;
          return String((field && field.options) || '').split(',').some(function (option) { return !!readWait(option); });
        } catch (e) { return false; }
      });
      box.csoEdDescriptionWait = describedWait(data.description);

      var checkbox = checkboxOf(box);
      if (checkbox) checkbox.addEventListener('change', function () { update(box); });

      if (window.MutationObserver) {
        var root = rootOf(box);
        new MutationObserver(function () { schedule(box); }).observe(root === document ? document.body : root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'required', 'hidden', 'disabled']
        });
      }

      /* The options app renders late and defaults are applied on a timer,
         so keep checking for the first 30 seconds as well. */
      var runs = 0;
      var poll = setInterval(function () {
        update(box);
        if (++runs >= 60) clearInterval(poll);
      }, 500);

      update(box);
    };

    api.setupAll = scheduleAll;
    document.addEventListener('DOMContentLoaded', scheduleAll);

    /* For checking the wording rules from the browser console. */
    api.readWait = readWait;
    api.describedWait = describedWait;
    api.formatWait = formatWait;
  }

  /* Set up every tick box on the page (this file also runs again when a
     product is loaded into a quick-add popup). */
  try { api.setupAll(); } catch (e) {}
})();
